import { useState } from 'react'
import { staffSessionV2Service } from '@/features/auth/staff-session-v2.service'
import { extractApiError } from '@/shared/utils/handle-api-error'
import useAuth from './use-auth'

export type StaffStepUpInput = {
  password: string
  verificationCode?: string
}

/**
 * Reabre la ventana de MFA reciente de la SESIÓN VIGENTE para operaciones sensibles del
 * perfil o de administración. Primero la contraseña; si falta el código, el backend
 * responde `MFA_REQUIRED` y se revela el campo del autenticador (TOTP). Los códigos de
 * recuperación son un fallback de *login*, no del step-up dentro de la plataforma.
 *
 * Antes esto se resolvía con un login nuevo, lo que traía dos defectos: re-autenticaba
 * con `auth.username` —mutable, y ya rompió una confirmación justo después de un cambio
 * de usuario— y creaba una sesión por confirmación, ensuciando la lista de dispositivos.
 * Ahora se confirma contra la sesión en uso, así que no hay identificador que sincronizar
 * ni sesiones fantasma.
 */
export function useStaffStepUp() {
  const { auth, setAuth } = useAuth()
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false)

  const reset = () => {
    setNeedsSecondFactor(false)
  }

  /**
   * Devuelve el access token de la sesión ya elevada. Si la cuenta requiere segundo factor
   * y todavía no se ingresó, revela el campo y devuelve `null` para que el llamador pida
   * reintentar; cualquier otro error se propaga.
   */
  const acquireAccessToken = async (input: StaffStepUpInput): Promise<string | null> => {
    if (!auth?.v2AccessToken) throw new Error('Missing staff session')
    try {
      const session = await staffSessionV2Service.reauthenticate(auth.v2AccessToken, {
        password: input.password,
        ...(needsSecondFactor ? { totp: input.verificationCode } : {})
      })
      // El backend refresca `mfaVerifiedAt` en la sesión, así que el token anterior ya
      // queda dentro de la ventana; el nuevo además trae el `amr` correcto, que viaja
      // firmado en el JWT. Guardarlo mantiene coherente al resto de la aplicación.
      setAuth((previous) =>
        previous ? { ...previous, v2AccessToken: session.accessToken } : previous
      )
      return session.accessToken
    } catch (error) {
      if (extractApiError(error)?.errorCode === 'MFA_REQUIRED') {
        setNeedsSecondFactor(true)
        return null
      }
      throw error
    }
  }

  return {
    needsSecondFactor,
    reset,
    acquireAccessToken,
    isMfaEnrolled: auth?.mfa?.enrolled ?? false
  }
}
